/*
  Customer-facing dismiss for the Shopify Inbox message bar.

  Inbox has no slot for a close button inside its shadow root, so this overlays
  a ✕ on the bar's top-right corner. Clicking it hides the widget for the rest
  of the browser SESSION (sessionStorage): it survives reloads and navigation in
  the same tab, and is gone when the tab closes — so a shopper can clear the bar
  out of the way without permanently losing the chat.

  Because the choice is remembered, it has to be reversible: while the bar is
  dismissed, an "Ask anything..." row appears in the Help menu (built with the
  widget's own .ifh-option class), and clicking it clears the flag and brings
  the bar back. The menu is the only place the bar can be recovered from, so the
  row is driven by the same sessionStorage key the ✕ writes.

  The ✕ does not appear until the bar has STOPPED MOVING. Inbox slides the bar
  in, and the theme's bottom-edge rules move it too, so parking the ✕ on the
  first rect we could measure put it on screen while the bar was still
  travelling — it arrived early, at the wrong offset, then jumped into place.
  Now it stays hidden until SETTLE_SAMPLES consecutive samples agree on the
  bar's rect (SETTLE_MS apart), and only then fades in on the settled corner.

  Timers are tracked so they can be restarted, not just cleared: clearing an
  interval without nulling its handle made startSettle() believe a settle loop
  was still running, so after the 60s wind-down a restored bar never got its ✕
  back.

  While the bar is on screen this also puts `if-inbox-visible` on <html>, which
  assets/if-inbox-layering.css uses to lift the Help popover clear of the bar.

  Read-only with respect to the app: it never talks to Inbox, it only hides the
  <shopify-chat> element and remembers the choice for the tab.
*/
(function () {
  var KEY = "if-inbox-dismissed";
  var SETTLE_MS = 120;
  var SETTLE_SAMPLES = 3;
  var SETTLE_GIVEUP_MS = 10000;
  // How far the ✕ pokes past the bar's top-right corner. Kept small so the
  // button sits mostly INSIDE the bar; 12 (half-in/half-out) read as floating
  // away from it.
  var X_OUT = 6;
  var root = document.documentElement;
  var btn = null;
  var restore = null;
  var tick = null;
  var settle = null;
  var mountPoll = null;
  var shown = false;
  var lastKey = "";
  var stable = 0;
  var waitStart = 0;

  function dismissed() {
    try {
      return window.sessionStorage.getItem(KEY) === "1";
    } catch (e) {
      return false;
    }
  }

  if (dismissed()) root.classList.add("if-inbox-dismissed");

  function chat() {
    return document.querySelector("shopify-chat");
  }

  function bar() {
    var c = chat();
    var b = c && c.shadowRoot ? c.shadowRoot.querySelector(".floating-input-launcher") : null;
    if (b) return b;
    // Once the chat has been opened and minimised, Inbox stops rendering a
    // .floating-input-launcher and puts the collapsed composer back as a
    // bar-sized .panel inside the runtime's shadow root — same rect, different
    // element (measured 708/16, 358x56). Without this the ✕ had nothing to sit
    // on after a shopper closed the chat, and stayed hidden for good.
    var p = panel();
    return p && p.getBoundingClientRect().height <= 80 ? p : null;
  }

/* ---------- same rail as the other two launchers ---------- */

  // Inbox places the message bar with
  //   bottom: max(--private-spacing-xl, env(safe-area-inset-bottom))
  //           + --private-preview-bar-offset + 6px
  // Every term there is constant EXCEPT env(safe-area-inset-bottom), and that is
  // exactly what a phone browser changes as its search/address bar shows and
  // hides while scrolling. #ifhRoot and the Collective launcher use a flat rail,
  // so the bar was the only one of the three that drifted on its own. Pinning the
  // bar to the same rail puts all three back on one anchor: they now move
  // together, and only with the layout viewport, like every other fixed element.
  // (A shadow-root style is the only way in — the light DOM cannot reach it.)
  var PIN_ATTR = "data-if-inbox-rail";

  function pinLauncher() {
    var c = chat();
    var sr = c && c.shadowRoot;
    if (!sr) return;
    if (sr.querySelector("style[" + PIN_ATTR + "]")) return;
    var s = document.createElement("style");
    s.setAttribute(PIN_ATTR, "");
    s.textContent =
      "@media screen and (min-width:768px){.floating-input-launcher{bottom:var(--if-inbox-desktop,20px) !important}}" +
      "@media screen and (min-width:768px){:host(:not([open])) .panel{bottom:var(--if-inbox-desktop,20px) !important}}" +
      "@media screen and (max-width:767px){.floating-input-launcher{bottom:var(--if-inbox-mobile,80px) !important}}" +
      "@media screen and (max-width:767px){:host(:not([open])) .panel{bottom:var(--if-inbox-mobile,80px) !important}}";
    sr.appendChild(s);
  }

  // While the chat is open Inbox removes the message bar from its shadow root,
  // so there is nothing for the ✕ to sit on. The panel is still reachable:
  // <shopify-chat> hosts shopify-agent-runtime, whose shadow root holds .panel.
  function panel() {
    var c = chat();
    var sr = c && c.shadowRoot;
    var rt = sr && sr.querySelector("shopify-agent-runtime");
    var rs = rt && rt.shadowRoot;
    return rs ? rs.querySelector(".panel") : null;
  }

  // The rail <style> lives in <shopify-chat>'s shadow root, but the collapsed
  // composer comes back as a .panel inside shopify-agent-runtime's OWN shadow
  // root, where that style cannot reach. The panel runs the same
  // env(safe-area-inset-bottom) formula, so without this it drifted 708 -> 694
  // under a 34px inset while the launcher stayed put. Pin it inline instead —
  // and only our own inline value is ever removed.
  var pinnedPanel = null;

  function pinPanelBottom(el) {
    if (el === pinnedPanel) return;
    if (pinnedPanel) pinnedPanel.style.removeProperty("bottom");
    pinnedPanel = el;
    if (!el) return;
    el.style.setProperty(
      "bottom",
      window.matchMedia("(max-width: 767px)").matches ? "var(--if-inbox-mobile, 80px)" : "var(--if-inbox-desktop, 20px)",
      "important",
    );
  }

  function bannerUp() {
    var b = document.querySelector("#shopify-pc__banner");
    return !!b && window.getComputedStyle(b).display !== "none";
  }

  function hide() {
    if (btn) btn.style.display = "none";
    root.classList.remove("if-inbox-visible");
  }

  function stopTimers() {
    if (settle) {
      clearInterval(settle);
      settle = null;
    }
    if (tick) {
      clearInterval(tick);
      tick = null;
    }
  }

  /* ---------- "Ask anything..." row inside the Help menu ---------- */

  function helpMain() {
    return document.querySelector("#ifhRoot .ifh-main");
  }

  function buildRestore() {
    if (restore) return;
    var main = helpMain();
    if (!main) return;
    restore = document.createElement("button");
    restore.type = "button";
    restore.className = "ifh-option ifh-chat-restore";
    restore.hidden = true;
    restore.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>' +
      "</svg>" +
      '<span class="ifh-option-label">Ask anything...</span>' +
      '<svg class="ifh-option-arrow" viewBox="0 0 24 24" aria-hidden="true">' +
      '<path d="m9 18 6-6-6-6"></path></svg>';
    restore.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      restoreChat();
    });
    var status = main.querySelector("#ifhStatus");
    if (status && status.nextSibling) main.insertBefore(restore, status.nextSibling);
    else main.appendChild(restore);
  }

  function syncRestore() {
    buildRestore();
    if (!restore) return;
    restore.hidden = !(dismissed() && chat());
  }

  function startSettle() {
    stopTimers();
    settle = window.setInterval(function () {
      if (place() && !tick) {
        clearInterval(settle);
        settle = null;
        tick = window.setInterval(place, 1000);
      }
    }, SETTLE_MS);
  }

  function restoreChat() {
    try {
      window.sessionStorage.removeItem(KEY);
    } catch (e) {}
    root.classList.remove("if-inbox-dismissed");
    shown = false;
    lastKey = "";
    stable = 0;
    waitStart = Date.now();
    hideToast();
    syncRestore();
    // Close the Help menu so the shopper sees the bar come back.
    var launcher = document.querySelector("#ifhLauncher");
    if (launcher && document.querySelector("#ifhRoot.is-open")) launcher.click();
    startSettle();
    place();
  }

  /* ---------- the ✕ ---------- */

  // Returns true once the button is placed on a settled bar.
  function place() {
    pinLauncher();
    if (!btn) return false;
    var c = chat();
    if (!c || dismissed() || bannerUp()) {
      hide();
      return false;
    }
    if (window.getComputedStyle(c).display === "none") {
      hide();
      return false;
    }

    // Chat open: no ✕ at all. The panel brings its own controls (expand and
    // minimise on desktop, minimise on a phone) and a second close button in
    // the same corner only competes with them. It comes straight back on
    // minimise — bar() falls through to the collapsed .panel below.
    if (c.hasAttribute("open")) {
      pinPanelBottom(null);
      hide();
      return false;
    }

    var b = bar();
    if (!b) {
      pinPanelBottom(null);
      hide();
      return false;
    }
    pinPanelBottom(b.classList && b.classList.contains("panel") ? b : null);
    var r = b.getBoundingClientRect();
    if (!r.width || !r.height || r.bottom < 0 || r.top > window.innerHeight) {
      hide();
      return false;
    }

    root.classList.add("if-inbox-visible");

    if (!shown) {
      var k =
        Math.round(r.left) + "," + Math.round(r.top) + "," + Math.round(r.width) + "," + Math.round(r.height);
      if (k === lastKey) stable++;
      else {
        lastKey = k;
        stable = 0;
      }
      // Still travelling (bar animating in): keep out of sight.
      if (stable < SETTLE_SAMPLES && Date.now() - waitStart < SETTLE_GIVEUP_MS) {
        hide();
        return false;
      }
      shown = true;
    }

    btn.style.display = "flex";
    // Tucked into the corner rather than straddling it: the ✕ pokes only
    // X_OUT past the bar's top and right edges (6px, was 12 — merchant: "make
    // it closer in to the bar"). Everything else about the button stays inside
    // the bar, which is what keeps it reading as the bar's own control.
    btn.style.left = Math.round(r.right - (btn.offsetWidth || 24) + X_OUT) + "px";
    // Anchored from the BOTTOM edge, not the top. Chrome's URL-bar
    // counter-scroll only applies to viewport-fixed BOTTOM layers, so a
    // top-anchored ✕ stays on the layout viewport while the bar and the two
    // pills ride the visible edge — it peels off the corner of the bar for the
    // length of the scroll and snaps back with it.
    //
    // offsetHeight, not a hardcoded 24: `bottom` is measured to the button's
    // own BOTTOM edge, so it has to know the button's height to reproduce the
    // same `top`. Using r.top alone (innerHeight - (r.top - 12)) put the whole
    // button ABOVE the bar — measured 674 where the old top rule gave 698.
    btn.style.top = "auto";
    btn.style.bottom =
      Math.round(window.innerHeight - (r.top - X_OUT + (btn.offsetHeight || 24))) + "px";
    return true;
  }

  // The message bar and Klaviyo's gift teaser deliberately do NOT hold still
  // during a scroll. They are plain fixed elements like #ifhRoot and the
  // Collective launcher, so all four move together as the browser chrome
  // collapses — a held bar reads as lagging behind the rest of the stack.
  /* ---------- the "where did it go" note ---------- */

  // Dismissing removes the only visible chat entry point, and the row that
  // brings it back lives inside the Help menu — which is not a place a shopper
  // would think to look for something called "chat". So the dismissal says
  // where it went, in the bar's own place, for a few seconds and then leaves.
  // It takes no taps (pointer-events: none) and is hidden whenever either menu
  // is open, so it can never sit in front of the thing it is pointing at.
  var TOAST_TEXT = "Ask anything hidden - open from this menu to chat...";
  var toast = null;
  var toastTimer = null;

  function hideToast() {
    if (toastTimer) {
      clearTimeout(toastTimer);
      toastTimer = null;
    }
    if (toast) toast.classList.remove("is-shown");
  }

  function showToast() {
    if (!toast) {
      toast = document.createElement("div");
      toast.className = "if-inbox-toast";
      toast.setAttribute("role", "status");
      toast.setAttribute("aria-live", "polite");
      toast.textContent = TOAST_TEXT;
      document.body.appendChild(toast);
    }
    // Reflow so a second dismissal replays the fade rather than being a no-op.
    void toast.offsetWidth;
    toast.classList.add("is-shown");
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = window.setTimeout(hideToast, 7000);
  }

  /* ---------- Keyboard: no second dose of bottom padding ---------- */

  // The open panel sits at `bottom: panel-bottom-offset + keyboard-offset`
  // (MEASURED: panel-bottom-offset resolves to 72px — the 20px rail inset plus
  // this store's launcher rail), so with a phone keyboard up the composer ends
  // 72px above it — dead space that reads as bottom padding (merchant,
  // 2026-11-02). Inbox itself already tracks the keyboard: faking a 350px
  // visual-viewport shrink sets `--private-keyboard-offset: 350px` on the panel
  // with no help from us. What is left is the rail offset, and that offset only
  // exists to clear the Help and Collective pills while the bar is COLLAPSED.
  //
  // So the fix is to zero the CSS variable Inbox derives its bottom from —
  // `--private-preview-bar-offset` — rather than writing `bottom` / `max-height`
  // / `padding-bottom` onto `.panel` as the first cut did. Inbox then keeps
  // sizing the panel itself: MEASURED with a 350px keyboard, bottom 370 (= 20 +
  // 350) and max-height 474, with nothing inline on `.panel` at all.
  //
  // The first cut rewrote three inline properties on `.panel` every 300ms,
  // including while Android animates the keyboard open, and the merchant came
  // back with "clicking the bar now I have to click again to get the keyboard
  // up". Nothing here touches the focused iframe any more, and it only acts
  // once the keyboard has stopped moving (two identical samples).
  function keyboardHeight() {
    var vv = window.visualViewport;
    if (!vv || vv.scale > 1.2) return 0; // pinch zoom is not a keyboard
    return Math.max(0, Math.round(window.innerHeight - (vv.height + vv.offsetTop)));
  }

  var kbdLast = -1;
  var kbdSame = 0;

  function keyboardOffset() {
    var c = chat();
    if (!c) return;
    var mobile = !window.matchMedia || window.matchMedia("(max-width: 767px)").matches;
    var kbd = mobile && c.hasAttribute("open") ? keyboardHeight() : 0;
    if (kbd === kbdLast) kbdSame += 1;
    else {
      kbdLast = kbd;
      kbdSame = 0;
    }
    var want = kbd > 120 && kbdSame >= 1;
    // Read the attribute back rather than trusting a local flag: if Inbox ever
    // re-creates the host, a flag would go on believing the var was still set.
    var cur = c.style.getPropertyValue("--private-preview-bar-offset");
    if (want && cur !== "0px") c.style.setProperty("--private-preview-bar-offset", "0px");
    else if (!want && cur) c.style.removeProperty("--private-preview-bar-offset");
  }

  window.setInterval(keyboardOffset, 250);
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", keyboardOffset);
    window.visualViewport.addEventListener("scroll", keyboardOffset);
  }
  document.addEventListener("focusin", keyboardOffset, true);
  document.addEventListener("focusout", function () {
    window.setTimeout(keyboardOffset, 120);
  }, true);

  function dismiss() {
    try {
      window.sessionStorage.setItem(KEY, "1");
    } catch (e) {}
    root.classList.add("if-inbox-dismissed");
    shown = false;
    hide();
    syncRestore();
    showToast();
  }

  function mount() {
    pinLauncher();
    if (btn || !bar()) return;
    btn = document.createElement("button");
    btn.type = "button";
    btn.className = "if-inbox-dismiss";
    btn.setAttribute("aria-label", "Hide chat");
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>';
    btn.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      dismiss();
    });
    document.body.appendChild(btn);
    waitStart = Date.now();
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, { passive: true });

    // Opening and closing the chat swaps the bar for the panel and back. Watch
    // the host's own "open" attribute so the ✕ is re-placed on the swap rather
    // than waiting for a scroll — the 1 Hz tick stops after a minute, and a
    // shopper who opens the chat later would otherwise never get it back.
    if (window.MutationObserver) {
      // The observer also takes the composer overlay out on the same frame the
      // chat opens, rather than leaving it up until the next 200ms paint.
      new MutationObserver(function () {
        startSettle();
        if (chat().hasAttribute("open")) dropComposerTyper();
      }).observe(chat(), { attributes: true, attributeFilter: ["open"] });
    }

    // Sample quickly until the bar settles, then drop back to a slow re-place.
    startSettle();

    clearInterval(mountPoll);
    mountPoll = null;
  }

  /* ---------- typewriter prompt ---------- */

  // Inbox renders "Ask anything..." as a real span inside the launcher button
  // (shadow DOM), not as a placeholder attribute — but it is React-rendered and
  // rewrites that span's text on its own schedule, which is why putting the
  // typed text INSIDE it failed: every re-render restored "Ask anything..."
  // for up to a hold's worth of a tick (merchant: "the typewrite words are
  // overwritten after a second").
  //
  // So the typed text lives in an overlay that React does not own — an
  // absolutely positioned span inside `.floating-input-launcher` (which is
  // position: fixed, so it is the containing block), aligned to the real
  // placeholder's offset. The native text is hidden by a `:has(.if-typer)` rule,
  // so if our overlay is ever missing the real placeholder shows through again
  // rather than going blank. The button's accessible name comes from
  // aria-label="Open chat", so none of this changes what a screen reader says.
  // Theme settings > Bottom edge > Typewriter questions wins; this list is what
  // runs when the merchant leaves that field empty (theme.liquid hands the
  // setting over as window.ifTyperPrompts, one question per line).
  // Each line maps to something Shopify's buyer AI can actually answer from
  // this store: /pages/size-guide (flat-lay measurements, "best fits" ranges),
  // the Shipping and Returns policies, /pages/payments-promos-gift-cards
  // (Afterpay), /pages/store-locations (three Gold Coast stores with phones
  // and a stock-varies FAQ), and the catalogue (New Arrivals, Summer Fashion
  // collections). Ordered as the shopper's journey: discover, fit, delivery,
  // pay, after-care. Keep every line under ~30 characters — the bar truncates
  // past that. NZ shipping and "Where is my order?" were dropped on the
  // merchant's call (2026-11-02).
  var DEFAULT_PROMPTS = [
    "What's new this week?",
    "Show me summer dresses...",
    "What size should I order?",
    "How much is shipping?",
    "Do you accept Afterpay?",
    "Can I return an item?",
    "Where are your stores?",
  ];

  function promptList() {
    var raw = window.ifTyperPrompts;
    var lines =
      typeof raw === "string"
        ? raw.split(/\r?\n/)
        : Object.prototype.toString.call(raw) === "[object Array]"
          ? raw
          : [];
    var clean = [];
    for (var i = 0; i < lines.length && clean.length < 8; i++) {
      var s = String(lines[i] == null ? "" : lines[i]).trim();
      if (s) clean.push(s);
    }
    return clean.length ? clean : DEFAULT_PROMPTS;
  }

  var PROMPTS = promptList();
  var TYPE_MS = 65;
  // Was 1800/420: the merchant asked for the prompt to sit on screen longer
  // between words (2026-11-02). Hold is now long enough to read the shortest
  // prompt twice over, and the gap after an erase is longer too.
  var HOLD_MS = 4200;
  var ERASE_MS = 28;
  var GAP_MS = 900;
  var SPARK =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"></path>' +
    '<path d="M18.4 15.4l.6 1.6 1.6.6-1.6.6-.6 1.6-.6-1.6-1.6-.6 1.6-.6z"></path></svg>';
  var TYPE_CSS =
    ".if-typer{position:absolute;display:flex;align-items:center;gap:6px;pointer-events:none;" +
    "color:#432c2c;opacity:.8;font-size:16px;overflow:hidden}" +
    ".floating-input-launcher:has(.if-typer) .floating-input-launcher__placeholder{color:transparent !important}" +
    // Masked mode: the minimised composer is a CROSS-ORIGIN iframe, so its label
    // cannot be made transparent. The overlay paints the pill's own colour over
    // it instead, and MUST be fully opaque — the base .if-typer runs at opacity
    // .8, which let the native "Ask anything..." show through the mask on live
    // (merchant, 2026-11-02).
    ".if-typer--masked{background:var(--if-typer-mask,#f5f4f4);border-radius:8px;font-size:17px;opacity:1}" +
    ".if-typer-spark{display:inline-flex;align-items:center;flex:none}" +
    ".if-typer-spark svg{width:15px;height:15px}" +
    ".if-typer-text{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}" +
    ".if-typer-text::after{content:'';display:inline-block;width:1px;height:1em;margin-left:1px;" +
    "background:currentColor;vertical-align:-2px;animation:if-caret 1s steps(1) infinite}" +
    "@keyframes if-caret{0%,49%{opacity:1}50%,100%{opacity:0}}" +
    "@media (prefers-reduced-motion: reduce){.if-typer-text::after{animation:none}}";
  var typing = {
    idx: 0,
    len: 0,
    phase: "type",
    timer: null,
    started: false,
    reduced: false,
    goneAt: 0, // when the launcher last left the screen (the chat was opened)
  };

  // The overlay lives inside <shopify-chat>'s shadow root for the bar, and
  // inside shopify-agent-runtime's NESTED shadow root for the minimised
  // composer. A <style> in one shadow root cannot reach the other, so each root
  // gets its own copy.
  function typeStyle(rootNode) {
    var c = chat();
    var sr = rootNode || (c && c.shadowRoot);
    if (!sr || sr.querySelector("style[data-if-typer]")) return;
    var s = document.createElement("style");
    s.setAttribute("data-if-typer", "");
    s.textContent = TYPE_CSS;
    sr.appendChild(s);
  }

  function buildTyper() {
    var wrap = document.createElement("span");
    wrap.className = "if-typer";
    wrap.innerHTML =
      '<span class="if-typer-spark">' + SPARK + "</span>" + '<span class="if-typer-text"></span>';
    return wrap;
  }

  // Once the chat has been opened and minimised, Inbox never renders
  // .floating-input-launcher again: the collapsed composer comes back as a
  // bar-sized .panel whose only visible child is a CROSS-ORIGIN iframe
  // (`sandbox="allow-scripts allow-same-origin allow-downloads"`, and it
  // redirects off this origin, so contentDocument is null). There is no
  // placeholder element to hide — which is why the prompts vanished for good
  // after the first visit to the chat (merchant, 2026-11-02). The overlay goes
  // over the iframe instead and masks its label.
  function composerBox() {
    var c = chat();
    // `open` covers the whole opening animation, when the panel is still short
    // enough to look like the collapsed composer.
    if (!c || c.hasAttribute("open")) return null;
    var p = panel();
    if (!p) return null;
    var r = p.getBoundingClientRect();
    if (!r.width || r.height > 80 || r.height < 24) return null;
    var body = p.querySelector(".panel__body");
    return (body && body.getBoundingClientRect().width ? body : p) || null;
  }

  // Returns the text span, (re)building and re-aligning the overlay as needed.
  function typerText() {
    var c = chat();
    var sr = c && c.shadowRoot;
    var bar = sr && sr.querySelector(".floating-input-launcher");
    // Gone or laid out to nothing (the chat panel is up): treat as off screen
    // so paint() can tell an open from a re-render.
    if (!bar || !bar.getClientRects().length) return composerTyper();
    dropComposerTyper();
    typeStyle();
    var wrap = bar.querySelector(".if-typer");
    if (!wrap) {
      wrap = buildTyper();
      bar.appendChild(wrap);
    }
    wrap.classList.remove("if-typer--masked");
    wrap.style.removeProperty("right");
    wrap.style.removeProperty("bottom");
    wrap.style.removeProperty("background");
    var ph = bar.querySelector(".floating-input-launcher__placeholder");
    if (ph) {
      wrap.style.left = ph.offsetLeft + "px";
      wrap.style.top = ph.offsetTop + "px";
      wrap.style.height = ph.offsetHeight + "px";
      wrap.style.maxWidth = Math.max(80, bar.clientWidth - ph.offsetLeft - 56) + "px";
    }
    return wrap.querySelector(".if-typer-text");
  }

  // The composer overlay lives INSIDE .panel__body, so opening the chat leaves it
  // floating over the conversation as a translucent ghost unless it is taken out
  // again (merchant, 2026-11-02: "the overlay is see through and stays over the
  // chat window when opened"). Nothing else removes it, so drop it explicitly
  // whenever the composer is not the thing on screen.
  function dropComposerTyper() {
    var c = chat();
    var sr = c && c.shadowRoot;
    var rt = sr && sr.querySelector("shopify-agent-runtime");
    var rs = rt && rt.shadowRoot;
    if (!rs) return;
    var w = rs.querySelector(".if-typer--masked");
    if (w && w.parentNode) w.parentNode.removeChild(w);
  }

  // The composer's pill is painted by the IFRAME, not by the panel: sampled on
  // live 2026-11-02 the pill is #f5f4f4 while .panel is #fafafa, and masking
  // with the panel's colour left a paler band over the bar (merchant: "the white
  // bg is visible over the bar"). There is no way to read a colour inside a
  // cross-origin frame, so this constant IS the pill; if Inbox's chat theme
  // changes, re-sample it (the `--if-typer-mask` var remains as the override).
  function composerTyper() {
    var host = composerBox();
    if (!host) {
      dropComposerTyper();
      return null;
    }
    typeStyle(host.getRootNode());
    var wrap = host.querySelector(".if-typer");
    if (!wrap) {
      wrap = buildTyper();
      host.appendChild(wrap);
    }
    if (!wrap.classList.contains("if-typer--masked")) wrap.classList.add("if-typer--masked");
    if (window.getComputedStyle(host).position === "static") host.style.position = "relative";
    // MEASURED at 390x844: the composer is 16,726 358x56 and its label starts
    // 20px inside the pill (absolute x 36), vertically centred. The mask is
    // inset from every edge so it stays on the pill's straight sides: at y=6
    // the pill's own left edge is only 8px in, so 18px clears the corner, and
    // 64px on the right stays well clear of the send arrow.
    wrap.style.left = "18px";
    wrap.style.right = "64px";
    wrap.style.top = "6px";
    wrap.style.bottom = "6px";
    wrap.style.height = "auto";
    wrap.style.maxWidth = "";
    return wrap.querySelector(".if-typer-text");
  }

  function currentText() {
    return PROMPTS[typing.idx % PROMPTS.length].slice(0, typing.len);
  }

  // Painting is deliberately separate from the type/erase timing and runs on a
  // slow interval of its own. It is the thing that survives an Inbox re-render:
  // if the launcher's children were ever replaced wholesale, this puts the
  // overlay and the current word back within 200ms instead of waiting out a
  // 1.8s hold.
  function paint() {
    var t = typerText();
    var here = !!t;
    // Opening the chat swaps the launcher out for the panel. When it comes
    // back, start the prompts over from the first one instead of resuming
    // wherever the loop happened to be (merchant: "when opening the chat and
    // closing again can the typewrite start again"). A short absence is not an
    // open — Inbox also swaps the node out for a frame during its own
    // re-renders — so only a gap of 350ms or more counts.
    if (typing.started) {
      if (here) {
        if (typing.goneAt && Date.now() - typing.goneAt > 350) resetTyping();
        typing.goneAt = 0;
      } else if (!typing.goneAt) {
        typing.goneAt = Date.now();
      }
    }
    if (!here) return;
    var s = currentText();
    if (t.textContent !== s) t.textContent = s;
  }

  function resetTyping() {
    if (typing.timer) {
      clearTimeout(typing.timer);
      typing.timer = null;
    }
    typing.idx = 0;
    typing.len = 0;
    typing.phase = "type";
    if (typing.reduced) {
      typing.len = PROMPTS[0].length;
      return;
    }
    typing.timer = window.setTimeout(typeTick, 450);
  }

  function typeTick() {
    var word = PROMPTS[typing.idx % PROMPTS.length];
    var wait = TYPE_MS;
    if (typing.phase === "type") {
      typing.len++;
      if (typing.len >= word.length) {
        typing.len = word.length;
        typing.phase = "hold";
        wait = HOLD_MS;
      }
    } else if (typing.phase === "hold") {
      typing.phase = "erase";
      wait = ERASE_MS;
    } else {
      typing.len--;
      if (typing.len <= 0) {
        typing.len = 0;
        typing.phase = "type";
        typing.idx++;
        wait = GAP_MS;
      } else {
        wait = ERASE_MS;
      }
    }
    paint();
    typing.timer = window.setTimeout(typeTick, wait);
  }

  function startTyping() {
    typing.reduced =
      !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    typing.started = true;
    if (typing.reduced) {
      typing.len = PROMPTS[0].length;
      paint();
    } else {
      typing.timer = window.setTimeout(typeTick, 1200);
    }
    window.setInterval(paint, 200);
  }

  place();
  syncRestore();
  startTyping();

  // The Help menu is where a dismissed bar is recovered, so keep that row
  // accurate whenever the menu is opened.
  document.addEventListener(
    "click",
    function (e) {
      if (e.target && e.target.closest && e.target.closest("#ifhLauncher")) {
        window.setTimeout(syncRestore, 0);
      }
    },
    true,
  );

  mountPoll = window.setInterval(function () {
    mount();
    syncRestore();
  }, 500);
  window.setTimeout(function () {
    clearInterval(mountPoll);
    mountPoll = null;
    stopTimers();
  }, 60000);
})();
